'use client';

import { useEffect, useRef, useState } from 'react';

import { useConfigValue } from '@/components/providers/config-provider';
import { safeSessionStorage } from '@/lib/storage/safe-storage';
import type { AffiliateConfig } from '@/lib/types/tenant';
import { AFFILIATE_STORAGE_KEY, resolveAffiliateId } from '@/lib/utils/affiliate';
import { clearUrlParam, getUrlParam } from '@/lib/utils/url-params';

export function useAffiliateTracking(): string | null {
  const affiliateConfig = useConfigValue<AffiliateConfig>('navigation.affiliateConfig');
  const paramName = affiliateConfig?.paramName || 'ref';

  const [affiliateId, setAffiliateId] = useState<string | null>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    const urlParam = getUrlParam(paramName);
    const validated = resolveAffiliateId({
      value: urlParam,
      config: affiliateConfig ?? undefined,
    });
    const storedValue = safeSessionStorage.getItem(AFFILIATE_STORAGE_KEY);
    const validatedStored = resolveAffiliateId({
      value: storedValue,
      config: affiliateConfig ?? undefined,
    });

    if (storedValue && !validatedStored) {
      safeSessionStorage.removeItem(AFFILIATE_STORAGE_KEY);
    }

    const resolvedId = validated ?? validatedStored;

    if (!isInitialized.current || resolvedId !== affiliateId) {
      isInitialized.current = true;
      setAffiliateId(resolvedId);
    }

    if (validated && validated !== validatedStored) {
      safeSessionStorage.setItem(AFFILIATE_STORAGE_KEY, validated);
    }

    if (urlParam) {
      clearUrlParam(paramName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, affiliateConfig]);

  return affiliateId;
}
